import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/** Horizon's product-price element is not in the import map; it sits beside this file. */
const PRODUCT_PRICE_MODULE = new URL('./product-price.js', import.meta.url).href;

/**
 * How the shopper last used the page: 'keyboard' after a key press that moves or activates, 'pointer'
 * after a tap or a click, 'none' before either (a drawer restored open on page load). Read when the
 * drawer opens, to decide where focus lands (cart loop 5).
 * @type {'keyboard' | 'pointer' | 'none'}
 */
let inputModality = 'none';
document.addEventListener(
  'keydown',
  (event) => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    inputModality = 'keyboard';
    // The panel was focused for a pointer open; from a key press on, its ring may show again.
    for (const el of document.querySelectorAll('[data-pointer-focus]')) delete /** @type {HTMLElement} */ (el).dataset.pointerFocus;
  },
  true
);
document.addEventListener('pointerdown', () => (inputModality = 'pointer'), true);

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, the installments CTA close-on-click, and the quick view.
 *
 * Quick view (cart loop 3, DECISIONS 2026-09-15): the + on a "You may also like" tile, or a plain
 * click on the tile, swaps the drawer's content for that piece, as Nour Hammour's does. The piece's
 * product form, variant picker and price are lifted from its product page, as quick-add.js does,
 * so Horizon's own components handle variant changes, availability, errors and the cart event.
 * Back, or Escape, returns to the cart with focus on the tile that opened it; a successful add
 * returns to the cart scrolled to the top, where the new line is. The empty cart's grid of four
 * opens it the same way (cart loop 4).
 *
 * The foot (cart loop 4, DECISIONS 2026-09-14): Checkout shows it is busy from the first click until
 * the browser leaves for checkout, and ignores any further click, so an order is never sent twice.
 * While a quantity change, a removal or an add is in flight, the line it touches and the Total dim.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener('click', this.#handleCheckoutClick);
    this.addEventListener('click', this.#handleQuickViewClick);
    this.addEventListener('keydown', this.#handleQuickViewKeydown);
    this.#quickView?.addEventListener(StandardEvents.cartLinesUpdate, this.#handleQuickViewAdd);
    this.#dialog?.addEventListener('close', this.#handleDialogClose);
    window.addEventListener('pageshow', this.#handlePageShow);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener('click', this.#handleCheckoutClick);
    this.removeEventListener('click', this.#handleQuickViewClick);
    this.removeEventListener('keydown', this.#handleQuickViewKeydown);
    this.#quickView?.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleQuickViewAdd);
    this.#dialog?.removeEventListener('close', this.#handleDialogClose);
    window.removeEventListener('pageshow', this.#handlePageShow);
    window.clearTimeout(this.#checkoutBusyTimer);
    this.#quickViewAbort?.abort();
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    if (this.#isQuickViewOpen) this.#closeQuickView({ restoreFocus: false });
    this.#updateStickyState();
    this.#settleOpenFocus();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * <theme-drawer> puts focus on the close mark when it opens. From the keyboard that stays, ring and
   * all. After a tap or a click, or on a drawer restored open with the page, focus goes to the panel
   * itself instead, as Nour Hammour's does: the close mark showed a square ring to shoppers who never
   * pressed a key (cart loop 5). Tab from the panel still reaches the close mark first. Run again on
   * the next frame, for the restore path, where <theme-drawer> can focus the close mark after this.
   */
  #settleOpenFocus() {
    if (inputModality === 'keyboard') return;
    const dialog = this.#dialog;
    if (!dialog) return;
    if (!dialog.hasAttribute('tabindex')) dialog.tabIndex = -1;
    const settle = () => {
      if (inputModality === 'keyboard' || !dialog.open) return;
      const active = document.activeElement;
      const onClose = active instanceof HTMLElement && dialog.contains(active) && active.matches('.cart-drawer__close');
      if (!onClose && dialog.contains(active)) return;
      dialog.dataset.pointerFocus = '';
      dialog.focus({ preventScroll: true });
    };
    settle();
    requestAnimationFrame(settle);
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    // Stock and the recommendations change with the cart: never reuse a product page fetched before.
    this.#productPages.clear();
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;
    this.#markUpdating(event);

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  /* ---------------------------------------------------------------------------------------------
   * Foot: Checkout busy, lines and Total dimmed while the cart updates
   * ------------------------------------------------------------------------------------------- */

  /** @type {number} */
  #pendingUpdates = 0;

  /** @type {number} */
  #checkoutBusyTimer = 0;

  /**
   * The line a cart change touches (matched on its key) and the Total dim until the cart answers.
   * An add from elsewhere names no line in the drawer yet, so only the Total dims.
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #markUpdating(event) {
    const promise = event.promise;
    if (!promise) return;
    /** @type {Array<{ id?: string | number }>} */
    const lines = Array.isArray(event.lines) ? event.lines : [];
    const rows = lines
      .map((line) => (line && line.id != null ? String(line.id) : ''))
      .filter(Boolean)
      .flatMap((key) => [
        ...this.querySelectorAll(`.cart-drawer__inner .cart-items__table-row[data-key="${CSS.escape(key)}"]`),
      ]);

    this.#pendingUpdates += 1;
    this.setAttribute('data-cart-busy', '');
    for (const row of rows) row.setAttribute('data-cart-line-busy', '');

    const settle = () => {
      for (const row of rows) row.removeAttribute('data-cart-line-busy');
      this.#pendingUpdates = Math.max(0, this.#pendingUpdates - 1);
      if (this.#pendingUpdates === 0) this.removeAttribute('data-cart-busy');
    };
    promise.then(settle, settle);
  }

  /**
   * The first click on Checkout marks it busy and lets the form go to checkout; any click after that
   * is ignored. It is not disabled, which would cancel the submission it is waiting on.
   * @param {MouseEvent} event
   */
  #handleCheckoutClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = /** @type {HTMLButtonElement | null} */ (
      target?.closest('.cart-drawer__summary .cart__checkout-button') ?? null
    );
    if (!button) return;
    if (button.hasAttribute('data-busy')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    if (button.disabled || event.defaultPrevented) return;

    button.setAttribute('data-busy', '');
    button.setAttribute('aria-busy', 'true');
    // If the browser never leaves (a blocked submission, a network failure), Checkout comes back.
    window.clearTimeout(this.#checkoutBusyTimer);
    this.#checkoutBusyTimer = window.setTimeout(() => this.#clearCheckoutBusy(), 15000);
  };

  /**
   * Back from checkout restores this page from the back-forward cache with Checkout still busy.
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    if (event.persisted) this.#clearCheckoutBusy();
  };

  #clearCheckoutBusy() {
    window.clearTimeout(this.#checkoutBusyTimer);
    for (const button of this.querySelectorAll('.cart__checkout-button[data-busy]')) {
      button.removeAttribute('data-busy');
      button.removeAttribute('aria-busy');
    }
  }

  /* ---------------------------------------------------------------------------------------------
   * Quick view
   * ------------------------------------------------------------------------------------------- */

  /** @type {Map<string, Promise<Document>>} */
  #productPages = new Map();

  /** @type {AbortController | null} */
  #quickViewAbort = null;

  /** The + or tile link that opened the quick view, and the product it names, for focus on return. */
  /** @type {{ element: HTMLElement, handle: string, kind: 'quick' | 'link' } | null} */
  #quickViewOrigin = null;

  /** @returns {HTMLElement | null} */
  get #quickView() {
    return this.querySelector('[data-cart-quick-view]');
  }

  get #isQuickViewOpen() {
    return this.dataset.view === 'quick';
  }

  /**
   * @param {string} name
   * @returns {HTMLElement | null}
   */
  #qv(name) {
    return this.#quickView?.querySelector(`[data-cart-qv-${name}]`) ?? null;
  }

  /**
   * A click on a tile's + opens the quick view; so does a plain click on the tile's link. A click
   * with a modifier (new tab, new window) or any other button still follows the link.
   * @param {MouseEvent} event
   */
  #handleQuickViewClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || event.defaultPrevented) return;

    // A sold-out size is struck through and cannot be chosen here.
    if (target.matches('.cart-qv input[type="radio"][aria-disabled="true"]')) {
      event.preventDefault();
      return;
    }

    if (target.closest('[data-cart-qv-back]')) {
      event.preventDefault();
      this.#closeQuickView({ restoreFocus: true });
      return;
    }

    // The row under the lines, and the empty cart's grid of four (cart loop 4).
    const quick = /** @type {HTMLElement | null} */ (target.closest('.cart-recs .cart-recs__quick[data-quick-view]'));
    const link = quick
      ? null
      : /** @type {HTMLAnchorElement | null} */ (target.closest('.cart-recs .cart-recs__link'));
    if (!quick && !link) return;
    if (link && (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;

    const item = (quick ?? link)?.closest('.cart-recs__item');
    const button = /** @type {HTMLElement | null} */ (quick ?? item?.querySelector('.cart-recs__quick[data-quick-view]') ?? null);
    const url = button?.dataset.productUrl || link?.getAttribute('href') || '';
    if (!item || !url) return;

    event.preventDefault();
    const element = /** @type {HTMLElement} */ (quick ?? link);
    this.#openQuickView(item, url, { element, handle: button?.dataset.productHandle ?? '', kind: quick ? 'quick' : 'link' });
  };

  /**
   * Escape in the quick view goes back to the cart. Handled before <theme-drawer>'s document
   * listener, which leaves an Escape already handled (defaultPrevented) alone.
   * @param {KeyboardEvent} event
   */
  #handleQuickViewKeydown = (event) => {
    if (event.key !== 'Escape' || event.isComposing || event.defaultPrevented || !this.#isQuickViewOpen) return;
    event.preventDefault();
    this.#closeQuickView({ restoreFocus: true });
  };

  #handleDialogClose = () => {
    if (this.#isQuickViewOpen) this.#closeQuickView({ restoreFocus: false });
  };

  /**
   * @param {Element} item - The recommended tile.
   * @param {string} url - The product's URL.
   * @param {{ element: HTMLElement, handle: string, kind: 'quick' | 'link' }} origin
   */
  async #openQuickView(item, url, origin) {
    const quickView = this.#quickView;
    if (!quickView) {
      window.location.assign(url);
      return;
    }

    this.#quickViewAbort?.abort();
    const abort = new AbortController();
    this.#quickViewAbort = abort;
    this.#quickViewOrigin = origin;

    // What the tile already shows goes in straight away; the product page fills in the rest.
    this.#resetQuickView();
    const title = this.#qv('title');
    if (title) title.textContent = item.querySelector('.cart-recs__title')?.textContent?.trim() ?? '';
    const price = this.#qv('price');
    if (price) price.textContent = item.querySelector('.cart-recs__price')?.textContent?.trim() ?? '';
    const visit = /** @type {HTMLAnchorElement | null} */ (this.#qv('visit'));
    if (visit) visit.href = url;
    const count = this.#qv('count');
    // The empty cart has no count in its head: it is (0).
    if (count) count.textContent = this.querySelector('.cart-drawer__inner .cart-drawer__count')?.textContent?.trim() ?? '(0)';
    const logoSlot = this.#qv('logo');
    const logo = this.querySelector('.cart-drawer__inner .cart-drawer__logo');
    if (logoSlot && logo) logoSlot.replaceChildren(logo.cloneNode(true));
    const tileImage = item.querySelector('.cart-recs__media img');
    const media = this.#qv('media');
    if (media && tileImage) {
      const image = /** @type {HTMLImageElement} */ (tileImage.cloneNode(true));
      image.loading = 'eager';
      image.sizes = '(min-width: 750px) 400px, 88vw';
      media.replaceChildren(image);
    }

    this.dataset.view = 'quick';
    quickView.hidden = false;
    this.#qv('scroller')?.scrollTo({ top: 0, behavior: 'instant' });
    title?.focus({ preventScroll: true });

    try {
      const [doc] = await Promise.all([this.#fetchProductPage(url, abort.signal), this.#loadProductModules()]);
      if (abort.signal.aborted || !this.#isQuickViewOpen) return;
      const filled = this.#fillQuickView(doc);
      if (!filled) window.location.assign(url);
    } catch (error) {
      if (abort.signal.aborted) return;
      console.warn('[cart-drawer] Quick view could not load the product:', error);
      window.location.assign(url);
    }
  }

  /**
   * @param {string} url
   * @param {AbortSignal} signal
   * @returns {Promise<Document>}
   */
  #fetchProductPage(url, signal) {
    let page = this.#productPages.get(url);
    if (!page) {
      page = fetch(url, { signal })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .then((text) => new DOMParser().parseFromString(text, 'text/html'));
      page.catch(() => this.#productPages.delete(url));
      this.#productPages.set(url, page);
    }
    return page;
  }

  #loadProductModules() {
    return Promise.all([
      import('@theme/product-form'),
      import('@theme/variant-picker'),
      import(PRODUCT_PRICE_MODULE).catch(() => null),
    ]);
  }

  /**
   * Moves the product page's price, variant picker and product form into the quick view.
   * @param {Document} doc
   * @returns {boolean} False when the page has no product form to add from (an enquiry-only piece).
   */
  #fillQuickView(doc) {
    const grid = doc.querySelector('[data-product-grid-content]') ?? doc;
    const sourceForm = grid.querySelector('product-form-component');
    if (!sourceForm) return false;

    /** @param {Element} node */
    const adopt = (node) => {
      const copy = /** @type {HTMLElement} */ (document.importNode(node, true));
      // The product page could be the page underneath: keep every id, and what points at one, unique.
      for (const el of [copy, ...copy.querySelectorAll('[id]')]) {
        if (el.id) el.id = `CartQuickView-${el.id}`;
      }
      for (const attr of ['for', 'form', 'aria-controls', 'aria-describedby', 'aria-labelledby']) {
        for (const el of copy.querySelectorAll(`[${attr}]`)) {
          const value = el.getAttribute(attr) ?? '';
          el.setAttribute(attr, value.split(/\s+/).filter(Boolean).map((id) => `CartQuickView-${id}`).join(' '));
        }
      }
      return copy;
    };

    const form = adopt(sourceForm);
    // The fly-to-cart picture would fly to a header hidden under the drawer.
    form.querySelector('add-to-cart-component')?.setAttribute('data-add-to-cart-animation', 'false');

    const sourcePicker = grid.querySelector('variant-picker');
    const picker = sourcePicker ? adopt(sourcePicker) : null;
    // Never rewrite the address bar of the page underneath on a size change.
    if (picker) picker.dataset.templateProductMatch = 'false';

    const sourcePrice = grid.querySelector('product-price');
    const sourceImage = grid.querySelector('.product-media img, img.product-media__image');

    if (sourcePrice) this.#qv('price')?.replaceChildren(adopt(sourcePrice));
    this.#qv('options')?.replaceChildren(...(picker ? [picker] : []));
    this.#qv('form')?.replaceChildren(form);

    const media = this.#qv('media');
    if (media && sourceImage) {
      const image = /** @type {HTMLImageElement} */ (document.importNode(sourceImage, true));
      image.removeAttribute('id');
      image.removeAttribute('class');
      image.loading = 'eager';
      image.sizes = '(min-width: 750px) 400px, 88vw';
      const current = media.querySelector('img');
      image.alt = current?.alt ?? image.alt;
      media.replaceChildren(image);
    }

    if (picker) this.#preselectAvailableSize(picker);
    return true;
  }

  /**
   * The first size that can be bought is the one selected, as Nour Hammour's quick view opens.
   * Horizon already checks the first available variant; this covers a page that checked a sold-out one.
   * @param {HTMLElement} picker
   */
  #preselectAvailableSize(picker) {
    const fieldsets = picker.querySelectorAll('fieldset');
    if (fieldsets.length !== 1) return;
    const checked = /** @type {HTMLInputElement | null} */ (fieldsets[0]?.querySelector('input[type="radio"]:checked'));
    if (checked && checked.dataset.optionAvailable !== 'false') return;
    const available = /** @type {HTMLInputElement | null} */ (
      fieldsets[0]?.querySelector('input[type="radio"][data-option-available="true"]')
    );
    available?.click();
  }

  /**
   * Horizon's product form dispatched its add. The button shows it is busy; once the cart has the
   * line, the drawer goes back to the cart, scrolled to the top where Shopify puts the newest line.
   * @param {Event} event
   */
  #handleQuickViewAdd = (event) => {
    const linesEvent = /** @type {import('@shopify/events').CartLinesUpdateEvent} */ (event);
    const button = /** @type {HTMLElement | null} */ (this.#quickView?.querySelector('[ref="addToCartButton"]') ?? null);
    const setBusy = (/** @type {boolean} */ busy) => {
      if (!button) return;
      // aria-busy tells assistive tech; data-busy is the styling hook.
      button.toggleAttribute('data-busy', busy);
      if (busy) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    };
    setBusy(true);

    linesEvent.promise
      ?.then(({ detail }) => {
        setBusy(false);
        if (detail?.didError || !this.#isQuickViewOpen) return;
        this.#announce(globalThis.Theme?.translations?.added ?? '');
        this.#closeQuickView({ restoreFocus: false });
        this.#showNewestLine();
      })
      .catch((error) => {
        setBusy(false);
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Quick view add failed:', error);
      });
  };

  /**
   * After the cart re-render lands, the scroller is at the top and focus is on the newest line.
   */
  #showNewestLine() {
    const inner = this.querySelector('.cart-drawer__inner');
    const settle = () => {
      const scroller = this.querySelector('.cart-drawer__inner .cart-drawer__content');
      scroller?.scrollTo({ top: 0, behavior: 'instant' });
      const first = /** @type {HTMLElement | null} */ (
        this.querySelector('.cart-drawer__inner .cart-items__table-row[data-key] a[href]')
      );
      const close = /** @type {HTMLElement | null} */ (this.querySelector('.cart-drawer__inner .cart-drawer__close'));
      (first ?? close)?.focus({ preventScroll: true });
      this.#updateStickyState();
    };
    settle();
    if (!inner) return;
    let timer = 0;
    const observer = new MutationObserver(() => {
      clearTimeout(timer);
      timer = window.setTimeout(() => {
        observer.disconnect();
        settle();
      }, 50);
    });
    observer.observe(inner, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 4000);
  }

  /**
   * @param {string} message
   */
  #announce(message) {
    const status = this.querySelector('[data-cart-qv-status]');
    if (!status || !message) return;
    status.textContent = message;
    window.setTimeout(() => {
      if (status.textContent === message) status.textContent = '';
    }, 3000);
  }

  /**
   * Empties the quick view back to its skeleton, so no product form from a previous piece lingers.
   */
  #resetQuickView() {
    for (const name of ['media', 'options', 'price', 'title']) this.#qv(name)?.replaceChildren();
    const form = this.#qv('form');
    const placeholder = this.#quickViewPlaceholder;
    if (form && placeholder) form.replaceChildren(placeholder);
  }

  /** The skeleton's disabled, busy Add button, kept to put back while the next piece loads. */
  /** @type {Element | null} */
  #placeholderButton = null;

  get #quickViewPlaceholder() {
    if (!this.#placeholderButton) this.#placeholderButton = this.#qv('form')?.querySelector('.cart-qv__placeholder') ?? null;
    return this.#placeholderButton;
  }

  /**
   * @param {{ restoreFocus: boolean }} options
   */
  #closeQuickView({ restoreFocus }) {
    const quickView = this.#quickView;
    this.#quickViewAbort?.abort();
    this.#quickViewAbort = null;
    delete this.dataset.view;
    if (quickView) quickView.hidden = true;
    this.#resetQuickView();

    const origin = this.#quickViewOrigin;
    this.#quickViewOrigin = null;
    if (!restoreFocus || !origin) return;
    const selector =
      origin.kind === 'quick'
        ? `.cart-recs__quick[data-product-handle="${CSS.escape(origin.handle)}"]`
        : `.cart-recs__item:has(.cart-recs__quick[data-product-handle="${CSS.escape(origin.handle)}"]) .cart-recs__link`;
    const target = origin.element.isConnected ? origin.element : /** @type {HTMLElement | null} */ (this.querySelector(selector));
    target?.focus();
  }

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
